import { useState, useEffect } from 'react'

interface CarouselImage {
  id: number
  src: string
  alt: string
  title?: string
  description?: string
  redirect_url?: string
}

interface CarouselProps {
  images: CarouselImage[]
  autoPlayInterval?: number
  bannerUrl?: string
  onSlideClick?: (image: CarouselImage) => void
}

const Carousel = ({ images, autoPlayInterval = 5000 , bannerUrl, onSlideClick}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)

  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length)
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [isAutoPlaying, images.length, autoPlayInterval])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 10000)
  }

  if (images.length === 0) {
    return (
      <div className="w-full h-screen bg-gray-800 flex items-center justify-center">
        <p className="text-gray-400">No images available</p>
      </div>
    )
  }
  const handleRedirect = (image: CarouselImage) => {
    if (onSlideClick) {
      onSlideClick(image)
    } else {
      const url = image.redirect_url || bannerUrl
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  }

  return (
    <div className="relative w-full aspect-[16/9] sm:aspect-[16/9] md:h-screen overflow-hidden group bg-black"> 
      {/* Main Image */}
      <div className="relative w-full h-full">
        {images.map((image, index) => {
          const isActive = index === currentIndex
          return (
            <div
              key={image.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                isActive ? 'opacity-100 pointer-events-auto z-10 cursor-pointer' : 'opacity-0 pointer-events-none z-0'
              }`}
              onClick={() => isActive && handleRedirect(image)}
            >
            <img
              src={image.src}
              alt={image.alt} 
              className="w-full  object-contain md:object-contain"
            />
            {(image.title || image.description) && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-4 sm:pb-8 md:pb-24">
                  {image.title && (
                    <h2 className="text-lg sm:text-2xl md:text-5xl lg:text-6xl font-bold text-white mb-1 sm:mb-2 md:mb-4 drop-shadow-md">
                      {image.title}
                    </h2>
                  )}
                  {image.description && (
                    <p className="text-xs sm:text-base md:text-xl text-gray-200 max-w-3xl line-clamp-2">
                      {image.description}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )})}
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 sm:p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 z-20"
            aria-label="Previous slide"
          >
            <svg
              className="w-4 h-4 sm:w-6 sm:h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 sm:p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 z-20"
            aria-label="Next slide"
          >
            <svg
              className="w-4 h-4 sm:w-6 sm:h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'w-6 sm:w-8 bg-primary-light'
                  : 'w-1.5 sm:w-2 bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Carousel

